"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { useState, useRef, useEffect, useMemo, useCallback, type ReactNode } from "react";

import { DeviceFrame } from "./components/device-frame";
import { DemoStage } from "./components/demo-stage";
import { NarrativeText } from "./components/narrative-text";
import { FeatureTabs } from "./components/feature-tabs";
import { DemoSkeleton } from "./components/demo-skeleton";
import { useLandingFrameMode } from "./components/demo-viewport-context";
import { DemoOrgModeContext } from "./components/demo-org-mode-context";
import { DemoNavContext } from "./components/demo-nav-context";
import { DemoOrgModeSwitch } from "./components/demo-org-mode-switch";
import { useDemoTour } from "./hooks/use-demo-tour";
import { cn } from "@/lib/utils";
import type { OrgType } from "@/lib/auth/resolve-org-type";
import type { DemoScript, FeatureTab } from "./types";
import { nextDemoTab } from "./lib/demo-tabs";
import { stepAfterOverlay } from "./lib/step-after-overlay";
import { stepIndexById } from "./lib/step-index-by-id";
import type { DemoOpener } from "./components/demo-hotspot-layer";

import { buildDashboardScript } from "./scripts/dashboard-script";
import { buildDashboardCollabScript } from "./scripts/dashboard-collab-script";
import { buildCreatorsScript } from "./scripts/creators-script";
import { buildProposalsScript } from "./scripts/proposals-script";
import { buildNotesScript } from "./scripts/notes-script";
import { buildFinanceScript } from "./scripts/finance-script";
import { buildCalendarScript } from "./scripts/calendar-script";
import { buildWorkflowScript } from "./scripts/workflow-script";
import { buildTasksScript } from "./scripts/tasks-script";
import { buildCampanhasScript } from "./scripts/campanhas-script";
import { buildCalculatorScript } from "./scripts/calculator-script";
import { buildPerformanceScript } from "./scripts/performance-script";
import { buildWhatsappScript } from "./scripts/whatsapp-script";
import { INTERACTIVE_DEMO_COPY as COPY } from "./index.copy";
import { localizedPath } from "@/lib/i18n/locale-path";
import { getLocale } from "@tami/core/intl";

const DashboardDemo = dynamic(
  () => import("./demos/dashboard-demo").then((m) => ({ default: m.DashboardDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const CreatorsDemo = dynamic(
  () => import("./demos/creators-demo").then((m) => ({ default: m.CreatorsDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const FinanceDemo = dynamic(
  () => import("./demos/finance-demo").then((m) => ({ default: m.FinanceDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const CalendarDemo = dynamic(
  () => import("./demos/calendar-demo").then((m) => ({ default: m.CalendarDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const WorkflowDemo = dynamic(
  () => import("./demos/workflow-demo").then((m) => ({ default: m.WorkflowDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const NotesDemo = dynamic(
  () => import("./demos/notes-demo").then((m) => ({ default: m.NotesDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const TasksDemo = dynamic(
  () => import("./demos/tasks-demo").then((m) => ({ default: m.TasksDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const CampanhasDemo = dynamic(
  () => import("./demos/campanhas-demo").then((m) => ({ default: m.CampanhasDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const CalculatorDemo = dynamic(
  () => import("./demos/calculator-demo").then((m) => ({ default: m.CalculatorDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const PerformanceDemo = dynamic(
  () => import("./demos/performance-demo").then((m) => ({ default: m.PerformanceDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);
const WhatsAppDemo = dynamic(
  () => import("./demos/whatsapp-demo").then((m) => ({ default: m.WhatsAppDemo })),
  { loading: () => <DemoSkeleton />, ssr: false },
);

const SCRIPT_BUILDERS: Record<FeatureTab, () => DemoScript> = {
  painel: buildDashboardScript,
  creators: buildCreatorsScript,
  notas: buildNotesScript,
  financas: buildFinanceScript,
  agenda: buildCalendarScript,
  equipe: buildWorkflowScript,
  conteudo: buildTasksScript,
  campanhas: buildCampanhasScript,
  calculadora: buildCalculatorScript,
  desempenho: buildPerformanceScript,
  whatsapp: buildWhatsappScript,
};

const COLLAB_SCRIPT_BUILDERS: Partial<Record<FeatureTab, () => DemoScript>> = {
  painel: buildDashboardCollabScript,
  calculadora: buildProposalsScript,
};

export interface InteractiveDemoProps {
  /** "section" é a seção da home; "page" é a página /demo. */
  readonly variant: "section" | "page";
  /** Tela inicial. Padrão: "painel". */
  readonly initialTab?: FeatureTab;
  /** Tipo de conta inicial. Padrão: "creator". */
  readonly initialOrgMode?: OrgType;
  /** Avisa quem monta quando a tela ou o tipo de conta muda. */
  readonly onStateChange?: (state: {
    tab: FeatureTab;
    orgMode: OrgType;
    byUser: boolean;
  }) => void;
  /**
   * Peças da casca da página — o voltar e a chamada de cadastro — montadas nas
   * pontas da linha de cima. Ficam aqui, e não na casca, porque é essa linha
   * que precisa caber num fôlego junto com o aparelho e as abas; duas barras
   * empilhadas estouravam a tela. Só a variante `page` as usa.
   */
  readonly chromeStart?: ReactNode;
  readonly chromeEnd?: ReactNode;
}

export function InteractiveDemo({
  variant,
  initialTab = "painel",
  initialOrgMode = "creator",
  onStateChange,
  chromeStart,
  chromeEnd,
}: InteractiveDemoProps) {
  const [activeTab, setActiveTab] = useState<FeatureTab>(initialTab);
  const [orgMode, setOrgMode] = useState<OrgType>(initialOrgMode);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Só a seção da home precisa disso: é o que liga o tour quando ela aparece.
    // Na página o tour começa por clique, e `isVisible` não é lido.
    if (variant !== "section") return;
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [variant]);

  // Sincroniza a tela e a conta quando as props mudam — é por aqui que o
  // botão voltar do navegador e um link colado chegam. Não chama
  // onStateChange: quem mandou a mudança foi a URL, avisá-la de volta seria
  // laço.
  useEffect(() => {
    setActiveTab(initialTab);
    setOrgMode(initialOrgMode);
  }, [initialTab, initialOrgMode]);

  /*
   * Abas cuja tela muda em Collab têm roteiro próprio: o painel ganha o eixo
   * do quadro, e a calculadora vira a lista de propostas — outra tela, não a
   * mesma com outro rótulo. Nas demais muda o shell (menu, rótulos, nome do
   * espaço) e o roteiro segue o mesmo.
   */
  const script = useMemo(
    () =>
      (orgMode === "collab" ? COLLAB_SCRIPT_BUILDERS[activeTab] : undefined)?.() ??
      SCRIPT_BUILDERS[activeTab](),
    [orgMode, activeTab],
  );
  /*
   * Fim do roteiro é fim de sessão, não recomeço: a demo passa para a próxima
   * aba visível no tipo de conta atual e recomeça lá. Chegando no fim da
   * fileira, volta para o Painel — o tipo de conta não muda sozinho, quem
   * escolhe isso é o switch.
   */
  const handleScriptComplete = useCallback(() => {
    const tab = nextDemoTab(activeTab, orgMode === "collab");
    setActiveTab(tab);
    onStateChange?.({ tab, orgMode, byUser: false });
  }, [activeTab, orgMode, onStateChange]);

  const tour = useDemoTour({
    script,
    onScriptComplete: handleScriptComplete,
    autoPlayWhen: variant === "section" && isVisible,
    // A home não tem botão para iniciar o tour — só a página tem. Reduced
    // motion não pode congelar a seção sem saída nenhuma; a página mantém o
    // padrão do hook.
    respectReducedMotion: variant === "section" ? false : undefined,
  });
  const player = tour.player;

  /*
   * Na página a pessoa navega clicando: o elemento da tela declara
   * `data-demo-open` com o passo que abre, e abrir é pular para esse passo —
   * é o passo atual que monta o modal. Guardo de onde ela saiu para o fechar
   * ter para onde voltar.
   */
  const returnStepRef = useRef(0);

  const handleOpen = useCallback(
    (opener: DemoOpener) => {
      const target = stepIndexById(script, opener.stepId);
      if (target === null) return;
      returnStepRef.current = player.currentIndex;
      player.goToStep(target);
    },
    [script, player],
  );

  /*
   * Fechar tem dois destinos, porque abrir teve duas origens. Na página quem
   * abriu foi a pessoa, e ela volta para de onde saiu. Na home quem abriu foi
   * o roteiro, e fechar segue a linha dele a partir da camada seguinte — o X
   * precisa responder nas duas, senão é botão desenhado.
   */
  const handleClose = useCallback(() => {
    player.goToStep(
      variant === "page"
        ? returnStepRef.current
        : stepAfterOverlay(script, player.currentIndex),
    );
  }, [player, script, variant]);

  const handleTabChange = useCallback(
    (tab: FeatureTab) => {
      setActiveTab(tab);
      onStateChange?.({ tab, orgMode, byUser: true });
    },
    [onStateChange, orgMode],
  );

  const handleOrgModeChange = (mode: OrgType) => {
    if (mode === orgMode) return;
    setOrgMode(mode);
    // A aba Creators não existe em conta de creator: sair do modo Collab nela
    // deixaria o palco vazio.
    if (mode === "creator" && activeTab === "creators") {
      setActiveTab("painel");
    }
    player.restart();
    onStateChange?.({
      tab: mode === "creator" && activeTab === "creators" ? "painel" : activeTab,
      orgMode: mode,
      byUser: true,
    });
  };

  const activeCallout = player.currentStep.callout;
  const activeCalloutId = activeCallout?.targetId;
  const activeCalloutText = activeCallout?.text ?? "";
  const frameMode = useLandingFrameMode();

  // O menu da tela replicada navega igual à fileira de abas: mesma função.
  const navValue = useMemo(
    () => ({ onNavigate: handleTabChange }),
    [handleTabChange],
  );

  const orgModeValue = useMemo(
    () => ({ orgMode, isCollab: orgMode === "collab" }),
    [orgMode],
  );

  return (
    <DemoOrgModeContext.Provider value={orgModeValue}>
      <DemoNavContext.Provider value={navValue}>
        <div
          ref={sectionRef}
          className={cn(
            "flex flex-col items-center gap-3 px-2 md:gap-4 md:px-0",
            variant === "page" && "gap-2 md:gap-3 lg:min-h-0 lg:flex-1",
          )}
        >
          {/*
            Na página os dois convivem na mesma linha — seletor de conta à
            esquerda, controles do passo à direita —, que é o que distribui o
            espaço em volta do aparelho. `flex-wrap` com `justify-center`
            garante que em tela estreita eles empilham centralizados, em vez de
            um ficar colado na borda. Na home não há controles, e o seletor
            segue centrado sozinho.
          */}
          <div
            className={cn(
              "flex w-full flex-wrap items-center gap-3",
              // 52px é a altura da linha na direção aprovada: alta o bastante
              // para o alvo de toque do segmentado, baixa o bastante para o
              // aparelho e as abas caberem na mesma tela.
              variant === "page" && "min-h-[52px] gap-5",
              // Na página a linha vai de ponta a ponta porque leva a casca (o
              // voltar e a chamada de cadastro) nas extremidades; o seletor e
              // os controles ficam no meio.
              variant === "page"
                ? "shrink-0 justify-center lg:flex-nowrap lg:justify-between"
                : "justify-center",
            )}
          >
            {chromeStart}

            <DemoOrgModeSwitch
              value={orgMode}
              onChange={handleOrgModeChange}
              size={variant === "page" ? "compact" : "marketing"}
            />

            {chromeEnd}
          </div>

          <div
            className={cn(
              "w-full",
              /*
               * A largura do aparelho passa a depender da altura disponível: o
               * conjunto mede ~0,64 de altura por unidade de largura (1040x666
               * medidos), então `altura * 1.56` é a largura que ainda cabe. Sem
               * isto, numa tela baixa o aparelho empurra a fileira de abas para
               * fora — que é justamente o que se quer evitar.
               */
              variant === "page" &&
                "lg:mx-auto lg:min-h-0 lg:max-w-[min(1040px,calc((100svh-190px)*1.56))]",
            )}
            onPointerDownCapture={variant === "page" ? tour.handleStageInteraction : undefined}
          >
            <DeviceFrame>
              <DemoStage
                // Trocar de tipo de conta remonta o palco: os alvos da câmera
                // mudam de lugar e o scroll herdado apontaria para o vazio.
                key={`${frameMode}-${orgMode}`}
                currentStep={player.currentStep}
                activeCalloutText={activeCalloutText}
                cameraMode={variant === "page" ? "off" : "zoom"}
                onOpen={variant === "page" ? handleOpen : undefined}
              >
                {activeTab === "painel" && (
                  <DashboardDemo activeCalloutId={activeCalloutId} />
                )}
                {activeTab === "creators" && (
                  <CreatorsDemo activeCalloutId={activeCalloutId} />
                )}
                {activeTab === "notas" && (
                  <NotesDemo activeCalloutId={activeCalloutId} />
                )}
                {activeTab === "conteudo" && (
                  <TasksDemo
                    activeCalloutId={activeCalloutId}
                    showCreateModal={player.currentStep.id.startsWith("tasks-modal")}
                    modalStepId={player.currentStep.id}
                    showRoteiro={player.currentStep.id.startsWith("tasks-roteiro")}
                    onClose={handleClose}
                  />
                )}
                {activeTab === "campanhas" && (
                  <CampanhasDemo
                    activeCalloutId={activeCalloutId}
                    showCreateModal={player.currentStep.id.startsWith("camp-modal")}
                    modalStepId={player.currentStep.id}
                    onClose={handleClose}
                  />
                )}
                {activeTab === "financas" && (
                  <FinanceDemo
                    activeCalloutId={activeCalloutId}
                    modalStepId={player.currentStep.id}
                    onClose={handleClose}
                  />
                )}
                {activeTab === "calculadora" && (
                  <CalculatorDemo
                    activeCalloutId={activeCalloutId}
                    showCreateModal={player.currentStep.id.startsWith("calc-modal")}
                    modalStepId={player.currentStep.id}
                    onClose={handleClose}
                  />
                )}
                {activeTab === "desempenho" && (
                  <PerformanceDemo activeCalloutId={activeCalloutId} />
                )}
                {activeTab === "agenda" && (
                  <CalendarDemo
                    activeCalloutId={activeCalloutId}
                    modalStepId={player.currentStep.id}
                    onClose={handleClose}
                  />
                )}
                {activeTab === "equipe" && (
                  <WorkflowDemo activeCalloutId={activeCalloutId} />
                )}
                {activeTab === "whatsapp" && (
                  <WhatsAppDemo
                    activeCalloutId={activeCalloutId}
                    currentStepId={player.currentStep.id}
                  />
                )}
              </DemoStage>
            </DeviceFrame>
          </div>

          {(variant === "section" || tour.mode === "tour") && (
            <NarrativeText
              step={player.currentStep}
              progress={player.progress}
              stepIndex={player.currentIndex}
              totalSteps={script.length}
            />
          )}

          <FeatureTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            className={cn("max-w-full px-1", variant === "page" && "shrink-0")}
          />

          {variant === "section" && (
            <Link
              href={`${localizedPath("/demo", getLocale())}?tela=${activeTab}&conta=${orgMode}`}
              prefetch={false}
              data-testid="demo-page-link"
              className="mt-1 inline-flex items-center gap-2 rounded-full border border-tami-green/60 px-5 py-2.5 text-sm font-semibold text-tami-green transition-colors hover:bg-tami-green hover:text-tami-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tami-green focus-visible:ring-offset-2 focus-visible:ring-offset-tami-dark"
            >
              {COPY.exploreOnYourOwn}
              <ArrowUpRight className="size-4" aria-hidden />
            </Link>
          )}
        </div>
      </DemoNavContext.Provider>
    </DemoOrgModeContext.Provider>
  );
}
